/**
 * reminderSpan — which part of a bell line did we actually understand as
 * timing? Walter 2026-09-26, on a line of his that showed no orange at all:
 *
 *   "How about just not highlighting the 5/17 if we are not processing it.
 *    We could just highlight every wed and sun"
 *
 * The principle is his; the facts turned out to be that we DO process it —
 * `every Wed and Sun 5/17 9am book squash` consumes `every Wed and Sun 5/17
 * 9am` and starts the weekly rule on 5/17. So the rule this implements is his
 * principle applied honestly: the wash covers exactly what the parser
 * consumed, no more. Anything we ignored stays plain, which makes the
 * highlight a readable answer to "did it understand me?".
 *
 * Until now the editor's wash keyed solely off the ": " that the tidy-up
 * inserts, so a line that had never been through the tidy-up — typed before
 * that existed, or pasted — showed nothing, even when the reminder was
 * perfectly well understood. (The textarea's SyntaxBackdrop already fell back
 * to the parser this way; the CM6 editor never did.)
 *
 * CACHED, and that is not an optimisation detail: CodeMirror rebuilds
 * decorations on every keystroke, every selection move and every scroll, for
 * every visible line. chrono parsing per line per rebuild would be felt while
 * typing. Lines carrying the canonical ": " never reach here at all — the
 * editor answers those without a parse.
 */
import { parseExistingBellLine } from './parseExistingBellLine';

const cache = new Map();
const MAX = 400;

/**
 * consumedSpan(afterBell) → { start, end } | null
 *
 * Offsets are into `afterBell` — the text following the 🔔 prefix. Returns
 * null when the line has no date/recurrence we recognised, which is the
 * "leave it plain" answer.
 */
export function consumedSpan(afterBell) {
  const text = String(afterBell ?? '');
  if (!text.trim()) return null;
  if (cache.has(text)) return cache.get(text);

  let span = null;
  try {
    const p = parseExistingBellLine(text);
    if (p && p.isReminder && p.kind === 'dated'
      && typeof p.replaceStart === 'number' && typeof p.replaceEnd === 'number'
      && p.replaceEnd > p.replaceStart) {
      // Parser offsets are in TRIMMED coordinates — shift by the leading
      // whitespace the caller still has in `text`.
      const lead = text.length - text.trimStart().length;
      const start = lead + p.replaceStart;
      const end = Math.min(text.length, lead + p.replaceEnd);
      if (end > start) span = { start, end };
    }
  } catch {
    span = null;   // a parser throw must never break rendering
  }

  if (cache.size >= MAX) cache.clear();   // crude, and plenty: these are lines on screen
  cache.set(text, span);
  return span;
}

export default consumedSpan;
